import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBaseUrl,
  selectToken,
  updateHashTables,
  updateLatestTimeslot,
} from './fastaSlice';

export interface HashTable {
  [key: string]: string | number | boolean;
  name: string; // product
  timeslot: number; // unix timestamp
  forecast_slot: string;
  effective_ts: number;
  is_available: boolean;
  completeness: number;
}

const FastaHashTablesServer = () => {
  const dispatch = useDispatch();
  const fastaBaseUrl = useSelector(selectBaseUrl);
  const fastaToken = useSelector(selectToken);
  const [hashTablesToKeep, setHashTablesToKeep] = useState<HashTable[]>([]);
  const [latestTimeslot, setLatestTimeslot] = useState<number | null>(null);

  // timer to refresh hashtables every minute
  const [pulse, setPulse] = useState(0);
  const [pulseInterval, setPulseInterval] = useState(1000 * 60);

  const getFastaHashTables = () => {
    const fastaHashes = sessionStorage.getItem('fastaHashes');
    if (fastaHashes == null) {
      sessionStorage.setItem('fastaHashes', JSON.stringify([]));
      return JSON.parse(JSON.stringify([]));
    } else {
      return JSON.parse(fastaHashes);
    }
  };

  const [fastaHashes, setFastaHashes] = useState(getFastaHashTables);

  const appendFastaHash = (val: HashTable) => {
    const fastaHashes = getFastaHashTables();
    fastaHashes.push(val);
    sessionStorage.setItem('fastaHashes', JSON.stringify(fastaHashes));
    const updatedFastaHashes = JSON.stringify(
      sessionStorage.getItem('fastaHashes'),
    );
    setFastaHashes(updatedFastaHashes);
  };

  const appendFastaHashes = (vals: HashTable[]) => {
    const fastaHashes = getFastaHashTables();
    const concatFastaHashes = fastaHashes.concat(vals);
    const uniqueFastaHashes = fastaHashes.filter(
      (item: HashTable, index: number, self: HashTable[]) =>
        index === self.findIndex((t: HashTable) => t.id === item.id),
    );
    sessionStorage.setItem('fastaHashes', JSON.stringify(concatFastaHashes));
    const updatedFastaHashes = sessionStorage.getItem('fastaHashes');
    setFastaHashes(updatedFastaHashes);
  };

  const filterFastaHashes = (hashes: HashTable[], product: string) => {
    // Filter out forecast hashes leaving just observations,
    // and sort so that latest observation is first

    console.log('filterFastaHashes');

    const observationHashes = hashes
      .filter((hash) => {
        return hash.forecast_slot === '' && hash.name === product;
      })
      .sort((a, b) => {
        if (a === b) {
          return 0;
        }
        if (!a) {
          return 1;
        }
        if (!b) {
          return -1;
        }
        return a.timeslot < b.timeslot ? 1 : -1;
      });

    const latest = observationHashes[0];

    // Filter forecast hashes, keeping only the CRR/ROA forecasts for the latest observation
    // (upto 10 forecasts per timeslot currently returned by API, i.e. 2.5 hours).
    const forecastHashes = hashes
      .filter((hash) => {
        return (
          hash.timeslot === latest.timeslot &&
          hash.name === product &&
          (product === 'crr' || product === 'roa') &&
          hash.forecast_slot !== ''
        );
      })
      .sort((a, b) => {
        if (a === b) {
          return 0;
        }
        if (!a) {
          return 1;
        }
        if (!b) {
          return -1;
        }
        return Number(a.forecast_slot) - Number(b.forecast_slot);
      });

    //  .sort((a, b) => Number(a.forecast_slot) - Number(b.forecast_slot));

    // Keep the most recent observation slots (at most 9, i.e. 2 hours)
    // and the latest forecast slots (at most 10) in an array
    return observationHashes
      .slice(0, Math.min(observationHashes.length, 9))
      .reverse()
      .concat(forecastHashes);
  };

  // Dispatch HashTablesToKeep to Redux state
  useEffect(() => {
    console.log(hashTablesToKeep);
    dispatch(updateHashTables(hashTablesToKeep));
  }, [hashTablesToKeep]);

  // Dispatch latestTimeslot to Redux state
  useEffect(() => {
    console.log(latestTimeslot);
    dispatch(updateLatestTimeslot(latestTimeslot));
  }, [latestTimeslot]);

  const fetchFastaHashes = async () => {
    const productCodes: { [index: string]: string } = {
      'Rain over Africa Rainfall Rate': 'roa',
      'Convective Rainfall Rate': 'crr',
      'Rapidly Developing Thunderstorms': 'rdt',
      Lightning: 'li',
    };

    const response = await fetch(
      `https://${fastaBaseUrl}/api/v1/vts/?token=${fastaToken}`,
    );
    const json = await response.json();

    let latestTs: number | null = null;
    const hashes: HashTable[] = [];

    json.tilesets.forEach((product: any) => {
      const name = productCodes[product.name];
      if (name === 'roa') {
        // The UI will need to know latest available timeslot to calibrate selector controls.
        // CRR and RDT may differ but CRR takes priority.
        latestTs = new Date(product.latest_timeslot)?.getTime();
      }

      product.timeslots.forEach((timeslot: any) => {
        const timestamp = new Date(timeslot.timeslot).getTime();
        const isLatest = timestamp === latestTs;
        const isAvailable = timeslot.available === 'Yes';
        const hash: HashTable = {
          name: name,
          timeslot: timestamp, // new Date(timestamp).toISOString(), //timeslot.timeslot,
          forecast_slot: '',
          effective_ts: timestamp,
          is_available: isAvailable,
          completeness: timeslot.product_completeness,
        };
        //console.log("Adding hash:" + timestamp);
        hashes.push(hash);

        timeslot.forecast_slots.forEach((forecast_slot: number) => {
          //console.log("Adding forecast hash:" + timestamp + ": " + forecast_slot.toString());
          const forecast_hash = { ...hash };
          forecast_hash.forecast_slot = forecast_slot.toString();
          forecast_hash.effective_ts = timestamp + forecast_slot * 60 * 1000;
          hashes.push(forecast_hash);
        });
      });
    });
    appendFastaHashes(hashes);
    console.log('fastaHashes:', hashes);
    const keepers = filterFastaHashes(hashes, 'roa')
      .concat(filterFastaHashes(hashes, 'crr'))
      .concat(filterFastaHashes(hashes, 'rdt'))
      .concat(filterFastaHashes(hashes, 'li'));

    setHashTablesToKeep(keepers);
    console.log('keepers:');

    setLatestTimeslot(latestTs);
  };

  useEffect(() => {
    fetchFastaHashes();
  }, []);

  useEffect(() => {
    const doPulse = () => setPulse((currentPulse) => currentPulse + 1);
    const interval = setInterval(doPulse, pulseInterval);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    console.log('AUTO REFRESH');
    fetchFastaHashes();
  }, [pulse]);

  return <div className="FastaHashTables"></div>;
};

export default FastaHashTablesServer;
